import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

export interface ExportExcelConfig<T> {
  fileName: string;
  data: T[] | Promise<T[]> | ((pageRequest: {pageSize: number, pageIndex: number}) => Promise<{items: T[], total: number}>);
  columns: ExportExcelColumn<T>[];
}


export interface ExportExcelColumn<T> {
  title: string;
  field: string;
  width?: number;
  transform?: (data: T, datas: T[]) => string;
  hidden?: boolean | ((datas: T[]) => boolean);
}

export interface ExportPageRequest {
  pageIndex: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root'
})
export class ExportService {

  public static ITEM_PER_PAGE = 10_000;

  constructor() {}

  async exportExcel<T>(config: ExportExcelConfig<T>) {
    if (config.data && config.columns) {
      const records = await this._getDataExport(config.data);

      const workbook = XLSX.utils.book_new();
      const columnShows = config.columns.filter(column => !column.hidden && !(typeof column.hidden === 'function' && column.hidden(records)));
      const headers: string[] = columnShows.map(column => column.title);
      const arrayOfArrays: any[][] = records.map(item => {
        return columnShows.map(column => {
          if (typeof column.transform === 'function') {
            return column.transform.apply(undefined, [item, records]) ?? '';
          }
          return item[column.field] ?? '';
        })
      });
      const worksheet = XLSX.utils.aoa_to_sheet([
        headers,
        ...arrayOfArrays,
      ]);
      worksheet["!cols"] = columnShows.map((column, index) => ({
        wpx: column.width ?? 100,
        wch: arrayOfArrays.reduce((w, r) => Math.max(w, String(r[index])?.length), 5),
      }));
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      XLSX.writeFile(workbook, `${config.fileName}`);
    }
  }

  private async _getDataExport<T>(data: T[] | Promise<T[]> | ((pageRequest: ExportPageRequest) => Promise<{items: T[], total: number}>)): Promise<T[]> {
    if (Array.isArray(data)) {
      return Promise.resolve(data);
    }

    if (data instanceof Promise) {
      return await data;
    }

    if (typeof data === 'function') {
      const items: T[] = [];
      let pageIndex = 0;
      let pageSize = ExportService.ITEM_PER_PAGE;
      let totalPage = 0;

      const getPageItemRecusiveFn = async (pageRequest: ExportPageRequest) => {
        const getPageResult = await data(pageRequest);
        if (getPageResult?.total && getPageResult?.items?.length) {
          totalPage = Math.ceil(getPageResult.total / pageSize);
          items.push(...getPageResult.items);
          if (pageIndex < totalPage - 1) {
            pageIndex++;
            await getPageItemRecusiveFn({pageIndex: pageIndex, pageSize: pageSize})
          }
        }
      }

      await getPageItemRecusiveFn({
        pageIndex, 
        pageSize
      });
      
      return items;
    }
  }
}